/**
 * Compliance audit document sources (CUR-2555): finding documents in an organisation's connected
 * SharePoint and adding copies to a compliance audit. Shared so the server gates and the client
 * agree on the flag.
 */
import type {
    DocumentSourceCapability,
    DocumentSourceConnectionStatus,
    DocumentSourceLibraryApproval,
    DocumentSourceProvider,
} from '@shared/prisma/enums';

/**
 * Default-off PostHog rollout flag. While it is off every document sources procedure answers
 * NOT_FOUND, and the configuration card and the audit's "Find documents" action are hidden.
 */
export const ENABLE_AUDIT_DOCUMENT_SOURCES_FLAG = 'enable-audit-document-sources';

/**
 * Default-off PostHog rollout flag for policy sources (CUR-2566; INSIGHT plan "Feature flag"), a sibling
 * of the document sources flag because an organisation may read policies without connecting SharePoint.
 * While it is off every policy source procedure answers NOT_FOUND and the organisation sees no policy
 * connection. Curium support's System Console screens are not gated by it.
 */
export const ENABLE_AUDIT_POLICY_SOURCES_FLAG = 'enable-audit-policy-sources';

/**
 * Why a connection needs attention (`DocumentSourceConnection.statusReason`), in the order a test
 * reports them: the first that applies is the one stored.
 *
 * - `CONSENT_MISSING`: the tenancy's admin has not accepted the consent link, or withdrew it.
 * - `CERTIFICATE_REJECTED`: Microsoft refused Curium's certificate (expired or replaced).
 * - `TENANT_NOT_FOUND`: no tenancy has the connection's tenant ID any more.
 * - `SIGN_IN_FAILED`: sign-in failed for another reason.
 * - `PERMISSION_MISSING`: signed in, but the token does not hold `Sites.Selected`.
 * - `NO_SITES`: no site has been added to the connection.
 * - `SITE_UNREACHABLE`: a site Curium was granted can no longer be opened.
 * - `SITE_LISTING_INCOMPLETE`: the last test could not list everything under a granted site (Microsoft
 *   refused a subsite or its libraries), so approvals on it wait.
 * - `APPROVED_LIBRARY_MISSING`: an approved library is no longer on its site.
 * - `LIBRARY_APPEARED`: a granted site has a library that was not there when its libraries were approved.
 * - `NO_APPROVED_LIBRARIES`: nothing is approved yet, so there is nothing Curium may search.
 * - `SOURCE_ERROR`: a source that signs in with the organisation's own credentials (an INSIGHT ledger)
 *   answered the test with an error Curium cannot place, or with something it cannot read (CUR-2566).
 *   Its refused sign-ins are `SIGN_IN_FAILED`, and an account it will not serve is `PERMISSION_MISSING`.
 */
export const DOCUMENT_SOURCE_STATUS_REASONS = [
    'CONSENT_MISSING',
    'CERTIFICATE_REJECTED',
    'TENANT_NOT_FOUND',
    'SIGN_IN_FAILED',
    'PERMISSION_MISSING',
    'NO_SITES',
    'SITE_UNREACHABLE',
    'SITE_LISTING_INCOMPLETE',
    'APPROVED_LIBRARY_MISSING',
    'LIBRARY_APPEARED',
    'NO_APPROVED_LIBRARIES',
    'SOURCE_ERROR',
] as const;

export type DocumentSourceStatusReason = (typeof DOCUMENT_SOURCE_STATUS_REASONS)[number];

/** How History names each reason a connection needs attention (CUR-2565). */
export const DOCUMENT_SOURCE_STATUS_REASON_LABELS = {
    CONSENT_MISSING: 'Consent not given',
    CERTIFICATE_REJECTED: 'Certificate refused',
    TENANT_NOT_FOUND: 'Tenancy not found',
    SIGN_IN_FAILED: 'Sign-in failed',
    PERMISSION_MISSING: 'Permission missing',
    NO_SITES: 'No sites',
    SITE_UNREACHABLE: 'A site can no longer be opened',
    SITE_LISTING_INCOMPLETE: 'A site could not be listed in full',
    APPROVED_LIBRARY_MISSING: 'An approved library is missing',
    LIBRARY_APPEARED: 'A library appeared after approval',
    NO_APPROVED_LIBRARIES: 'No approved libraries',
    SOURCE_ERROR: 'The source answered with an error',
} as const satisfies Record<DocumentSourceStatusReason, string>;

/**
 * CUR-2844 — the provider registry: every outside system a connection can read, with what Curium calls it
 * on screen, what its connections may supply, and whether Curium support confirms a new connection before
 * anything is read through it. A
 * screen names a source as its provider's display name and the connection's label ("SharePoint ·
 * Northside Risk Partners"); audit and template code never names a provider (the boundary test in
 * `server/src/services/domain/complianceAudit/__tests__/source-agnostic-boundary.test.ts`).
 */
export const DOCUMENT_SOURCE_PROVIDERS = {
    SHAREPOINT: {
        displayName: 'SharePoint',
        capabilities: ['DOCUMENTS'],
        // Consent and site grants belong to Curium's app, not to the organisation, so a connection alone
        // does not show which organisation the tenancy chose: support confirms it with the tenancy's admin.
        requiresSupportConfirmation: true,
    },
    STEADFAST_INSIGHT: {
        displayName: 'INSIGHT',
        capabilities: ['POLICIES', 'AUTHORISED_REPRESENTATIVES'],
        // A ledger's API user is the organisation's own, so its first successful test is the proof that
        // the organisation may read through it (CUR-2566).
        requiresSupportConfirmation: false,
    },
} as const satisfies Record<
    DocumentSourceProvider,
    {
        readonly displayName: string;
        readonly capabilities: readonly [DocumentSourceCapability, ...DocumentSourceCapability[]];
        readonly requiresSupportConfirmation: boolean;
    }
>;

/**
 * What a connection claims to supply that its provider cannot, each once; empty when the claim is sound.
 * Nothing calls this yet: `DocumentSourceConnections.create` copies the provider's capabilities from
 * this registry, so its `provides` cannot be unsound. The adapter that first takes `provides` from input
 * (CUR-2566) must call this and refuse a connection whose `provides` is empty or names anything returned
 * here; only the database's refusal of an empty `provides` is enforced today. A connection may supply
 * less than its provider can: an organisation may read only policies from a ledger that also holds
 * documents.
 */
export function unsupportedCapabilities(
    provider: DocumentSourceProvider,
    provides: readonly DocumentSourceCapability[],
): DocumentSourceCapability[] {
    const supported: readonly DocumentSourceCapability[] = DOCUMENT_SOURCE_PROVIDERS[provider].capabilities;
    return [...new Set(provides)].filter((capability) => !supported.includes(capability));
}

/** What Curium calls a provider on screen. */
export function documentSourceProviderName(provider: DocumentSourceProvider): string {
    return DOCUMENT_SOURCE_PROVIDERS[provider].displayName;
}

/** How History names a connection's status and a library's approval (CUR-2565). */
export const DOCUMENT_SOURCE_CONNECTION_STATUS_LABELS = {
    SETTING_UP: 'Setting up',
    CONNECTED: 'Connected',
    NEEDS_ATTENTION: 'Needs attention',
    DISABLED: 'Disabled',
} as const satisfies Record<DocumentSourceConnectionStatus, string>;

export const DOCUMENT_SOURCE_LIBRARY_APPROVAL_LABELS = {
    APPROVED: 'Approved',
    NOT_APPROVED: 'Not approved',
} as const satisfies Record<DocumentSourceLibraryApproval, string>;

/** The reasons a sign-in decides; the others follow from the sites and libraries Curium holds. */
export const DOCUMENT_SOURCE_SIGN_IN_REASONS = [
    'CONSENT_MISSING',
    'CERTIFICATE_REJECTED',
    'TENANT_NOT_FOUND',
    'SIGN_IN_FAILED',
    'PERMISSION_MISSING',
] as const satisfies readonly DocumentSourceStatusReason[];

/**
 * Below a granted site, Curium lists subsites this deep, and at most this many in all (CUR-2625). A
 * site with more is listed as far as these, and the page says the rest are not listed.
 */
export const DOCUMENT_SOURCE_MAX_SUBSITE_DEPTH = 3;
export const DOCUMENT_SOURCE_MAX_SUBSITES = 50;

/** At most this many sites are added to a connection in one request. */
export const DOCUMENT_SOURCE_MAX_SITES_PER_REQUEST = 20;

/** At most this many candidate decisions in one request; a search finds far fewer. */
export const DOCUMENT_SOURCE_MAX_CANDIDATE_DECISIONS = 500;

/** A search names at most this many libraries; the officer chooses which when a connection has more. */
export const DOCUMENT_SOURCE_MAX_COLLECTION_LIBRARIES = 50;

/** A search takes at most this many terms of the officer's own, each at most this long. */
export const DOCUMENT_SOURCE_MAX_EXTRA_TERMS = 10;
export const DOCUMENT_SOURCE_MAX_EXTRA_TERM_LENGTH = 100;

/**
 * At most this many candidates are kept from one search, the suggested first; a library that lost
 * any to the limit is reported as cut short. One library's crawl reads at most as many items.
 */
export const DOCUMENT_SOURCE_MAX_COLLECTION_CANDIDATES = 2000;
